import React, { AnchorHTMLAttributes, DetailedHTMLProps } from 'react'
import classNames from 'classnames'
import { Link, LinkProps } from 'react-router-dom'

export enum Variant {
  primary = 'primary',
  primaryLight = 'primaryLight',
  secondary = 'secondary',
  primaryOutlined = 'primaryOutlined',
  primaryLightOutlined = 'primaryLightOutlined',
  dialogPrimary = 'dialogPrimary',
  dialogSecondary = 'dialogSecondary',
}

export type ButtonProps = {
  variant: Variant
} & React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

export type ButtonInternalLinkProps = {
  variant: Variant
} & LinkProps

export type ButtonExternalLinkProps = {
  variant: Variant
} & DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>

const variantStyles: Record<
  Variant,
  { variantClass: string; variantDisabledClass: string }
> = {
  [Variant.primary]: {
    variantClass:
      'px-4 py-2 rounded-full text-white bg-black border-2 border-black hover:bg-gray-700 active:bg-gray-700 focus-visible:outline-primary-500',
    variantDisabledClass:
      'px-4 py-2 rounded-full text-gray-700 bg-gray-500 border-gray-500',
  },
  [Variant.primaryLight]: {
    variantClass:
      'px-4 py-2 rounded-full text-black bg-white border-2 border-white hover:bg-gray-300 active:bg-gray-300',
    variantDisabledClass:
      'px-4 py-2 rounded-full text-black bg-gray-500 border-2 border-gray-500',
  },
  [Variant.primaryOutlined]: {
    variantClass:
      "px-4 py-2 rounded-full text-back border-2 border-black hover:bg-gray-700 hover:text-white active:bg-gray-700 focus:text-white focus-visible:outline-primary-500'",
    variantDisabledClass:
      'px-4 py-2 rounded-full text-gray-500 border-gray-500',
  },
  [Variant.primaryLightOutlined]: {
    variantClass:
      'px-4 py-2 bg-gray-700 rounded-full text-white border-2 border-withe hover:bg-gray-600 active:bg-gray-600 focus-visible:outline-primary-500',
    variantDisabledClass:
      'px-4 py-2 rounded-full text-gray-500 border-2 border-gray-500',
  },
  [Variant.secondary]: {
    variantClass: 'text-black hover:underline active:underline',
    variantDisabledClass: 'text-gray-500',
  },
  [Variant.dialogPrimary]: {
    variantClass: classNames(
      'px-4 py-2 rounded-full focus-visible:outline-primary-500',
      'text-white bg-black border-2 border-black hover:bg-gray-700 active:bg-gray-700 ',
      'md:text-black md:border-white md:hover:bg-gray-300 md:active:bg-gray-300 md:bg-white'
    ),
    variantDisabledClass: classNames(
      'px-4 py-2 rounded-full',
      'text-gray-700 bg-gray-500 border-gray-500',
      'md:text-gray-500 md:border-gray-500 md:bg-gray-300'
    ),
  },
  [Variant.dialogSecondary]: {
    variantClass: classNames(
      'px-4 py-2 rounded-full focus-visible:outline-primary-500',
      'text-black border-2 border-black hover:bg-gray-700 hover:text-white active:bg-gray-700 focus:text-white',
      'md:bg-gray-700 md:text-white md:border-white md:hover:bg-gray-600 md:active:bg-gray-600'
    ),
    variantDisabledClass: classNames(
      'px-4 py-2 rounded-full',
      'text-gray-500 border-gray-500'
    ),
  },
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, type = 'button', className = '', children, ...props }, ref) => {
    return (
      <button
        className={classNames(
          'fill-current',
          props.disabled
            ? variantStyles[variant].variantDisabledClass
            : variantStyles[variant].variantClass,
          className
        )}
        ref={ref}
        type={type}
        {...props}
      >
        {children}
      </button>
    )
  }
)

const ButtonInternalLink: React.FC<ButtonInternalLinkProps> = ({
  variant,
  className = '',
  children,
  ...props
}) => {
  return (
    <Link
      className={classNames(
        'fill-current text-center flex justify-center items-center',
        variantStyles[variant].variantClass,
        className
      )}
      {...props}
    >
      {children}
    </Link>
  )
}

const ButtonExternalLink: React.FC<ButtonExternalLinkProps> = ({
  variant,
  className = '',
  children,
  ...props
}) => {
  return (
    <a
      className={classNames(
        'fill-current text-center flex justify-center items-center',
        variantStyles[variant].variantClass,
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
}

type ButtonIconProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  className?: string
  iconClassName?: string
}

export const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(
  function ButtonIcon(
    { icon: Icon, label, className, iconClassName, ...props },
    ref
  ) {
    return (
      <button
        className={classNames(
          'focus:outline-none fill-current p-2 rounded-full  focus:bg-gray-100 flex items-center justify-center',
          !props.disabled && 'hover:bg-gray-100',
          className
        )}
        aria-label={label}
        type="button"
        ref={ref}
        {...props}
      >
        <Icon className={classNames('h-6 w-6', iconClassName)} />
      </button>
    )
  }
)

export { Button, ButtonExternalLink, ButtonInternalLink }
